import { Vehicle } from '../types/vehicle';
import { vehicleService } from './vehicleService';
import { db } from '../config/firebase';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';

/**
 * Extension du service de véhicules avec des méthodes supplémentaires
 * pour l'importation CSV des consommations de carburant
 */
export class VehicleExtendedService {
  private readonly COLLECTION = 'vehicles';

  /**
   * Récupère un véhicule par sa plaque d'immatriculation
   * @param licensePlate Plaque d'immatriculation
   * @param organizationId ID de l'organisation
   * @returns Véhicule ou null si non trouvé
   */
  async getVehicleByLicensePlate(
    licensePlate: string,
    organizationId: string
  ): Promise<Vehicle | null> {
    try {
      // Normaliser la plaque d'immatriculation (supprimer les espaces, mettre en majuscules)
      const normalizedLicensePlate = licensePlate.trim().toUpperCase();
      
      // Créer la requête
      const q = query(
        collection(db, this.COLLECTION),
        where('organizationId', '==', organizationId),
        where('licensePlate', '==', normalizedLicensePlate)
      );
      
      // Exécuter la requête
      const querySnapshot = await getDocs(q);
      
      // Retourner le premier véhicule trouvé ou null
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() } as Vehicle;
    } catch (error) {
      console.error('Error getting vehicle by license plate:', error);
      throw new Error('Failed to get vehicle by license plate');
    }
  }

  /**
   * Met à jour le kilométrage d'un véhicule
   * @param vehicleId ID du véhicule
   * @param mileage Nouveau kilométrage
   * @param date Date du relevé
   * @param userId ID de l'utilisateur qui effectue la mise à jour
   * @returns true si le kilométrage a été mis à jour, false sinon
   */
  async updateVehicleMileage(
    vehicleId: string,
    mileage: number,
    date: Date,
    userId: string
  ): Promise<boolean> {
    try {
      // Récupérer le véhicule
      const vehicle = await vehicleService.getVehicle(vehicleId);
      
      if (!vehicle) {
        throw new Error(`Vehicle not found: ${vehicleId}`);
      }
      
      // Vérifier si le nouveau kilométrage est supérieur à l'ancien
      if (vehicle.currentMileage && mileage <= vehicle.currentMileage) {
        console.warn(`New mileage (${mileage}) is not greater than current mileage (${vehicle.currentMileage})`);
        return false;
      }
      
      // Mettre à jour le kilométrage du véhicule
      await vehicleService.updateVehicle(vehicleId, {
        currentMileage: mileage,
        lastMileageUpdate: Timestamp.fromDate(date)
      });
      
      // Ajouter un rapport de kilométrage
      await vehicleService.addMileageReport(vehicleId, {
        mileage,
        date: Timestamp.fromDate(date),
        reportedBy: userId,
        notes: 'Mise à jour automatique via importation CSV',
        validationStatus: 'validated'
      });
      
      return true;
    } catch (error) {
      console.error('Error updating vehicle mileage:', error);
      throw new Error('Failed to update vehicle mileage');
    }
  }
}

// Exporter une instance du service étendu
export const vehicleExtendedService = new VehicleExtendedService();